/**
 * NotificationBell — sino de notificações do Portal Cliente (Projetos 2.0 F3.2).
 *
 * Gated pela flag `features.projetosNotificacoes` (vem do GET /dashboard, que
 * reflete PORTAL_CLIENTE_PROJETOS_NOTIF_ENABLED). Flag OFF → componente não
 * renderiza nada (portal igual ao atual). Sem WebSocket no portal: polling 60s
 * via `useClienteNotificacoes`.
 *
 * UX: botão com badge de não-lidas + dropdown com as recentes. Clicar numa
 * notificação marca como lida e navega pro link (/cliente/projetos). Tem
 * "marcar todas como lidas" + link pra página completa.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  useClienteDashboard,
  useClienteNotificacoes,
  useMarcarNotificacaoLida,
  useMarcarTodasNotificacoesLidas,
  type ClienteNotificacao,
} from '@/lib/cliente-portal-store';

import { formatarData } from '@/lib/format-date';
function formatQuando(iso: string): string {
  try {
    return formatarData(iso, {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function NotificationBell() {
  const dash = useClienteDashboard();
  const featureOn = dash.data?.features?.projetosNotificacoes === true;
  const [open, setOpen] = useState(false);
  const { data } = useClienteNotificacoes(featureOn);
  const marcarLida = useMarcarNotificacaoLida();
  const marcarTodas = useMarcarTodasNotificacoesLidas();
  const navigate = useNavigate();

  if (!featureOn) return null;

  const items = data?.items ?? [];
  const naoLidas = data?.naoLidas ?? 0;

  function abrir(n: ClienteNotificacao) {
    if (!n.lida) marcarLida.mutate(n.id);
    setOpen(false);
    // F3.2: toda notificação é clicável — usa o link próprio ou cai nos projetos.
    navigate(n.linkUrl ?? '/cliente/projetos');
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative flex items-center justify-center w-9 h-9 rounded hover:bg-paper-warm dark:hover:bg-paper-dark-warm transition"
        aria-label="Notificações"
        data-testid="cliente-notif-bell"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 3a6 6 0 0 0-6 6c0 3-1 5-2 6h16c-1-1-2-3-2-6a6 6 0 0 0-6-6Z"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
          <path d="M9.5 19a2.5 2.5 0 0 0 5 0" stroke="currentColor" strokeWidth="1.6" />
        </svg>
        {naoLidas > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red text-white text-xs font-semibold flex items-center justify-center"
            data-testid="cliente-notif-badge"
          >
            {naoLidas > 9 ? '9+' : naoLidas}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* backdrop pra fechar ao clicar fora */}
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            className="fixed inset-0 z-10 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div
            className="absolute right-0 mt-1 w-80 max-w-[90vw] bg-paper-surface dark:bg-paper-dark-elevated border border-line dark:border-line-dark rounded shadow-lg z-20"
            data-testid="cliente-notif-dropdown"
          >
            <div className="flex items-center justify-between px-3 py-2 border-b border-line dark:border-line-dark">
              <span className="text-sm font-medium">Notificações</span>
              {naoLidas > 0 && (
                <button
                  type="button"
                  onClick={() => marcarTodas.mutate()}
                  className="text-xs text-red hover:underline"
                  data-testid="cliente-notif-marcar-todas"
                >
                  Marcar todas como lidas
                </button>
              )}
            </div>

            <div className="max-h-80 overflow-y-auto">
              {items.length === 0 ? (
                <p className="px-3 py-6 text-sm text-ink-3 dark:text-ink-dark-3 text-center">
                  Nenhuma notificação ainda.
                </p>
              ) : (
                <ul>
                  {items.slice(0, 8).map((n) => (
                    <li key={n.id}>
                      <button
                        type="button"
                        onClick={() => abrir(n)}
                        className={`w-full text-left px-3 py-2.5 border-b border-line dark:border-line-dark hover:bg-paper-warm dark:hover:bg-paper-dark-warm transition ${
                          n.lida ? 'opacity-70' : ''
                        }`}
                        data-testid={`cliente-notif-item-${n.id}`}
                      >
                        <div className="flex items-start gap-2">
                          {!n.lida && (
                            <span className="mt-1.5 w-2 h-2 rounded-full bg-red flex-shrink-0" />
                          )}
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
                              {n.title}
                            </div>
                            <div className="text-xs text-ink-2 dark:text-ink-dark-2">{n.body}</div>
                            <div className="text-xs text-ink-3 dark:text-ink-dark-3 mt-0.5">
                              {formatQuando(n.criadoEmIso)}
                            </div>
                          </div>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="px-3 py-2 border-t border-line dark:border-line-dark text-center">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate('/cliente/notificacoes');
                }}
                className="text-xs text-red hover:underline"
                data-testid="cliente-notif-ver-todas"
              >
                Ver todas
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
